/**
 * User Manager — list page (ORCH-336, ORCH-21 §12; PRD User Manager UM-1 to UM-3, UM-13)
 *
 * Purpose: The members of the tenant being managed, within the caller's
 *          `user.view` scope, with their organization, roles, security
 *          attributes, last sign-in to this tenant and status. From here an admin
 *          invites people, opens a person's page, and deactivates, reactivates or
 *          removes them, one at a time or in bulk (two or more ticked rows). An
 *          invitation is a row like any other (ORCH-420): it opens, it can be
 *          ticked, and its actions are Resend Invite and Cancel Invite.
 * Logic: Everything shown and changed is the membership in one tenant: the
 *        active tenant, or the one a system admin in GLOBAL picks. The server
 *        scopes the list, so people outside the caller's scope are neither shown
 *        nor counted, and it says per row what the caller may do (`_authz`).
 *        Which actions a row's status allows is decided once, in `memberActions`.
 * Dependencies: MDTable (metadata mode over `user_tenant`), RoleSelect,
 *               ConfirmDialog, InviteDialog, BulkAssignRolesDialog, memberActions,
 *               userManagementService, authzService
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Clear as ClearIcon,
  Home as HomeIcon,
  MailOutline as MailOutlineIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Groups as UsersIcon,
  Unsubscribe as UnsubscribeIcon,
  Visibility as VisibilityIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import SkeletonPage from '../../../components/layout/SkeletonPage';
import MDTable from '../../../components/md-table';
import {
  BulkAction,
  BulkActionBar,
  ConfirmDialog,
  NotificationSnackbar,
  RoleSelect,
  RowActionButton,
  RowActions,
  StatusChip,
} from '../../../components/common';
import InviteDialog, { InviteSummary } from '../../../components/user/InviteDialog';
import {
  attributeValues,
  auditPerson,
  firstLast,
  givenName,
  inviteSummary,
  lastFirst,
  MEMBERSHIP_MODEL_ID,
  membershipStatusLabel,
} from '../../../components/user/membership';
import {
  actionAppliesTo,
  BulkMemberAction,
  isBulkSelectable,
  MemberAction,
  mayAct,
  memberActions,
} from '../../../components/user/memberActions';
import { useMembershipFields } from '../../../components/user/useMembershipFields';
import { useAuth } from '../../../context/AuthContext';
import { PermissionKey } from '../../../constants/permissionKeys';
import { useDebounce } from '../../../utils/ui';
import { formatAuditTimestamp, formatAuditUser } from '../../../utils/formatting';
import userManagementService, { userErrorMessage } from '../../../services/userManagementService';
import { authzService } from '../../../services/authzService';
import { tenantService } from '../../../services/tenantService';
import { Role } from '../../../types/authz';
import {
  BulkInviteResult,
  BulkPasswordResetResult,
  BulkRolesResult,
  BulkStatusResult,
  Member,
  MemberFilter,
} from '../../../types/userManagement';
import { EFFECTIVE_ACCESS_TAB_PARAM } from '../RoleManager/RoleManagerPage';
import {
  BulkAssignRolesDialog,
  BulkRolesResultDialog,
  bulkRolesNotice,
} from './BulkAssignRolesDialog';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const MAX_ATTRIBUTE_CHIPS = 2;
// Shown on request from the column menu: the table is wide enough without it (ORCH-398)
const HIDDEN_BY_DEFAULT = { security_attributes: false };

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
  { value: 'inactive', label: 'Deactivated' },
];

const SKIP_REASONS: Record<string, string> = {
  LAST_SYSTEM_ADMIN: 'last System admin',
  OUT_OF_SCOPE: 'outside the people you manage',
  INVITE_PENDING: 'invitation not accepted yet',
  NOT_INVITED: 'already joined',
  NOT_FOUND: 'no longer in this tenant',
  // Bulk Reset Password: the single action's refusals (ORCH-387)
  ACCOUNT_INACTIVE: 'account deactivated',
  MEMBERSHIP_INACTIVE: 'deactivated in this tenant',
  SELF_RESET: "that's you",
  RESET_RECENTLY_SENT: 'a reset was sent under a minute ago',
  EMAIL_SEND_FAILED: "the email couldn't be sent",
};

/** The most people one bulk Reset Password emails: the server's PASSWORD_RESET_BULK_MAX. */
const PASSWORD_RESET_BULK_MAX = 100;

const users = (count: number) => `${count} ${count === 1 ? 'user' : 'users'}`;
const invitations = (count: number) => `${count} ${count === 1 ? 'invitation' : 'invitations'}`;
const joined = (count: number) =>
  count === 1 ? "1 person who's already joined" : `${count} people who've already joined`;

/** "2 users deactivated. 1 skipped (already deactivated)." (PRD UM-13, ORCH-382). */
export const bulkResultMessage = (
  result: BulkStatusResult,
  verb: string,
  noun: (count: number) => string = users
): string => {
  const message = `${noun(result.changed)} ${verb}.`;
  const skipped = result.unchanged + result.skipped.length;
  if (!skipped) return message;
  const reasons = Array.from(
    new Set([
      ...(result.unchanged ? [`already ${verb}`] : []),
      ...result.skipped.map((s) => SKIP_REASONS[s.reason] || s.reason),
    ])
  );
  return `${message} ${skipped} skipped (${reasons.join(', ')}).`;
};

/**
 * "Password reset email sent to 12 users. 3 skipped (invitation not accepted yet, account
 * deactivated)." Each distinct reason once (ORCH-387).
 */
export const resetResultMessage = (result: BulkPasswordResetResult): string => {
  const message = `Password reset email sent to ${users(result.sent.length)}.`;
  if (!result.skipped.length) return message;
  const reasons = Array.from(
    new Set(result.skipped.map((s) => SKIP_REASONS[s.reason] || s.reason))
  );
  return `${message} ${result.skipped.length} skipped (${reasons.join(', ')}).`;
};

/** Why an invite row's email didn't go out, and when, for the Email not sent tooltip (ORCH-419). */
export const inviteEmailNote = (
  member: Pick<Member, 'email_status' | 'email_status_at' | 'email_error'>
): string => {
  const reasons: Record<string, string> = {
    pending: 'Not sent yet.',
    failed: 'The mail server did not accept it.',
    not_configured: "Email isn't set up in this environment.",
    not_allowed: "This environment doesn't email that address.",
  };
  const reason = member.email_error?.trim() || reasons[member.email_status || 'pending'];
  return member.email_status_at
    ? `${reason} ${formatAuditTimestamp(member.email_status_at)}`
    : reason;
};

/**
 * The bulk status actions: what each did, and who it skips. Which rows each applies
 * to comes from the status matrix (`memberActions`): Deactivate acts on active
 * members, Reactivate on deactivated ones, Resend and Cancel on invitations, and Reset
 * Password on active ones (ORCH-387).
 */
const BULK_VERBS: Record<
  Exclude<BulkMemberAction, 'assignRoles'> | 'reset',
  { done: string; skipped: (count: number) => string; noun: (count: number) => string }
> = {
  deactivate: { done: 'deactivated', skipped: (n) => `${n} already deactivated`, noun: users },
  reactivate: { done: 'reactivated', skipped: (n) => `${n} already reactivated`, noun: users },
  reset: { done: 'sent a password reset', skipped: (n) => `${n} not active`, noun: users },
  resendInvite: {
    done: 'sent',
    skipped: joined,
    noun: invitations,
  },
  cancelInvite: {
    done: 'cancelled',
    skipped: joined,
    noun: invitations,
  },
};

type BulkKind = keyof typeof BULK_VERBS;

/** The matrix action a bulk kind acts through: Reset Password is the single `passwordReset`. */
const memberActionOf = (kind: BulkKind): MemberAction =>
  kind === 'reset' ? 'passwordReset' : kind;

type Confirm =
  | { kind: BulkKind; members: Member[]; allMatching: boolean }
  | { kind: 'remove'; member: Member }
  | { kind: 'cancelOne'; member: Member }
  | { kind: 'assign'; members: Member[]; allMatching: boolean };

/** "Invitation sent to jane@example.com", or why it wasn't (ORCH-419). */
export const resendNotice = (member: Member): InviteSummary =>
  member.email_status === 'sent'
    ? { message: `Invitation sent to ${member.email}.`, severity: 'success' }
    : {
        message: `Invitation renewed, but the email to ${member.email} was not sent: ${inviteEmailNote(member)}`,
        severity: 'warning',
      };

type MemberRow = Member & { id: string; name: string };

const UserManagementPage: React.FC = () => {
  const { state, can } = useAuth();
  const navigate = useNavigate();
  const activeTenantId = state.user?.activeTenant?.tenant_id || '';
  const isSystemAdmin = !!state.user?.is_system_admin;

  // A system admin in GLOBAL picks the tenant to manage; everyone else works in their own
  const [managedTenantId, setManagedTenantId] = useState(activeTenantId);
  const [tenantOptions, setTenantOptions] = useState<{ tenant_id: string; name: string }[]>([]);
  const tenantId = isSystemAdmin ? managedTenantId : activeTenantId;
  const otherTenant = tenantId !== activeTenantId ? tenantId : undefined;
  const tenantName =
    tenantOptions.find((t) => t.tenant_id === tenantId)?.name ||
    (tenantId === activeTenantId ? state.user?.activeTenant?.name : tenantId) ||
    'this tenant';

  const canInvite = can(PermissionKey.USER_CREATE);
  const canDeactivate = can(PermissionKey.USER_DEACTIVATE);
  const canEdit = can(PermissionKey.USER_EDIT);
  const canAssignRoles = can(PermissionKey.USER_ASSIGN_ROLES);
  const canModifyInvites = canInvite || can(PermissionKey.USER_EDIT);

  const [members, setMembers] = useState<Member[]>([]);
  const [total, setTotal] = useState(0);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<InviteSummary | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [roleId, setRoleId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [allMatching, setAllMatching] = useState(false);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [working, setWorking] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [rolesResult, setRolesResult] = useState<BulkRolesResult | null>(null);
  const [inviteResult, setInviteResult] = useState<{
    title: string;
    result: BulkInviteResult;
  } | null>(null);

  const { attributeFieldIds, valueName } = useMembershipFields(tenantId);

  useEffect(() => {
    setManagedTenantId(activeTenantId);
  }, [activeTenantId]);

  // The tenant list leaves GLOBAL out; a system admin is signed in to it, so it leads the picker
  useEffect(() => {
    if (!isSystemAdmin) return;
    const own = {
      tenant_id: activeTenantId,
      name: state.user?.activeTenant?.name || activeTenantId,
    };
    tenantService
      .getAllTenants()
      .then((tenants) =>
        setTenantOptions([
          own,
          ...tenants
            .filter((t) => t.tenant_id !== activeTenantId)
            .map((t) => ({ tenant_id: t.tenant_id, name: t.name })),
        ])
      )
      .catch(() => setTenantOptions([own]));
  }, [isSystemAdmin, activeTenantId, state.user?.activeTenant?.name]);

  // The managed tenant's role catalog feeds the role filter and the invite dialog's picker
  useEffect(() => {
    authzService
      .listRoles(undefined, otherTenant)
      .then(setRoles)
      .catch(() => setRoles([]));
  }, [activeTenantId, otherTenant]);

  const filter: MemberFilter = useMemo(
    () => ({
      search: search || undefined,
      status: status || undefined,
      role_id: roleId || undefined,
    }),
    [search, status, roleId]
  );

  const fetchMembers = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const data = await userManagementService.listUsers({
        ...filter,
        tenant_id: otherTenant,
        page,
        per_page: perPage,
      });
      setMembers(data.users);
      setTotal(data.total);
      setError(null);
    } catch (e) {
      setError(userErrorMessage(e, 'Failed to load users.'));
    } finally {
      setLoading(false);
    }
  }, [tenantId, otherTenant, filter, page, perPage]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // A selection belongs to the rows it was made on
  useEffect(() => {
    setSelectedIds([]);
    setAllMatching(false);
  }, [tenantId, filter, page, perPage]);

  const applySearch = useDebounce((value: string) => {
    setSearch(value.trim());
    setPage(1);
  }, 300);

  const openMember = useCallback(
    (member: Member) => {
      navigate(
        `/admin/user-management/${member.user_id}${otherTenant ? `?tenant=${encodeURIComponent(otherTenant)}` : ''}`
      );
    },
    [navigate, otherTenant]
  );

  const openEffectiveAccess = useCallback(
    (member: Member) =>
      navigate(`/admin/role-manager?tab=${EFFECTIVE_ACCESS_TAB_PARAM}`, {
        state: {
          member: {
            user_id: member.user_id,
            email: member.email,
            first_name: member.first_name ?? null,
            last_name: member.last_name ?? null,
            ...(otherTenant ? { tenant_id: otherTenant, tenant_name: tenantName } : {}),
          },
        },
      }),
    [navigate, otherTenant, tenantName]
  );

  const clearSelection = () => {
    setSelectedIds([]);
    setAllMatching(false);
  };

  // The server judges each person; a clean result is a notice, any skip opens the result list
  const assignRoles = async (roleIds: string[]) => {
    if (confirm?.kind !== 'assign') return;
    setWorking(true);
    try {
      const result = await userManagementService.bulkAssignRoles(
        confirm.allMatching ? filter : confirm.members.map((m) => m.user_id),
        roleIds,
        otherTenant
      );
      if (result.skipped.length) setRolesResult(result);
      else setNotice({ message: bulkRolesNotice(result), severity: 'success' });
      clearSelection();
      setConfirm(null);
      fetchMembers();
    } catch (e) {
      setConfirm(null);
      setError(userErrorMessage(e, 'The roles could not be assigned.'));
    } finally {
      setWorking(false);
    }
  };

  const reactivate = useCallback(
    async (member: Member) => {
      try {
        await userManagementService.setActive(member.user_id, tenantId, true);
        setNotice({
          message: `${givenName(member)} reactivated in ${tenantName}.`,
          severity: 'success',
        });
        fetchMembers();
      } catch (e) {
        setError(userErrorMessage(e, 'Could not reactivate this person.'));
      }
    },
    [tenantId, tenantName, fetchMembers]
  );

  // Resend needs no confirmation: it is harmless and undone by Cancel (ORCH-420 §2.2)
  const resendInvite = useCallback(
    async (member: Member) => {
      try {
        const sent = await userManagementService.resendInvitation(member.user_id, tenantId);
        setNotice(resendNotice(sent));
        fetchMembers();
      } catch (e) {
        setError(userErrorMessage(e, 'The invitation could not be resent.'));
      }
    },
    [tenantId, fetchMembers]
  );

  const handleConfirm = async () => {
    if (!confirm || confirm.kind === 'assign') return;
    setWorking(true);
    try {
      if (confirm.kind === 'remove') {
        await userManagementService.removeMember(confirm.member.user_id, tenantId);
        setNotice({
          message: `${firstLast(confirm.member)} removed from ${tenantName}.`,
          severity: 'success',
        });
      } else if (confirm.kind === 'cancelOne') {
        await userManagementService.cancelInvitation(confirm.member.user_id, tenantId);
        setNotice({
          message: `Invitation to ${firstLast(confirm.member)} cancelled.`,
          severity: 'success',
        });
      } else if (confirm.kind === 'resendInvite' || confirm.kind === 'cancelInvite') {
        const action = confirm.kind === 'resendInvite' ? 'resend' : 'cancel';
        const result = await userManagementService.bulkInvitations(
          action,
          confirm.allMatching ? filter : confirm.members.map((m) => m.user_id),
          otherTenant
        );
        // A clean result is a notice; a skip or an email that didn't go out opens the result list
        if (result.skipped.length || result.email_failed.length) {
          setInviteResult({
            title: action === 'resend' ? 'Invitations Resent' : 'Invitations Cancelled',
            result,
          });
        } else {
          setNotice({
            message: bulkResultMessage(result, BULK_VERBS[confirm.kind].done, invitations),
            severity: 'success',
          });
        }
      } else if (confirm.kind === 'reset') {
        const result = await userManagementService.bulkSendPasswordReset(
          confirm.allMatching ? filter : confirm.members.map((m) => m.user_id),
          otherTenant
        );
        setNotice({ message: resetResultMessage(result), severity: 'success' });
      } else if (confirm.members.length === 1 && !confirm.allMatching) {
        await userManagementService.setActive(confirm.members[0].user_id, tenantId, false);
        setNotice({
          message: `${givenName(confirm.members[0])} deactivated in ${tenantName}.`,
          severity: 'success',
        });
      } else {
        // The server says per person what changed; the selection is sent whole
        const result = await userManagementService.bulkSetActive(
          confirm.allMatching ? filter : confirm.members.map((m) => m.user_id),
          confirm.kind === 'reactivate',
          otherTenant
        );
        setNotice({
          message: bulkResultMessage(result, BULK_VERBS[confirm.kind].done),
          severity: 'success',
        });
      }
      clearSelection();
      setConfirm(null);
      fetchMembers();
    } catch (e) {
      setConfirm(null);
      setError(userErrorMessage(e, 'The change could not be made.'));
    } finally {
      setWorking(false);
    }
  };

  const rows: MemberRow[] = useMemo(
    () => members.map((m) => ({ ...m, id: m.user_id, name: lastFirst(m) })),
    [members]
  );

  const columnsBefore: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        minWidth: 160,
        flex: 1,
        renderCell: (params) => (
          <Typography variant="body2" data-testid="user-name">
            {(params.row as MemberRow).name}
          </Typography>
        ),
      },
      { field: 'email', headerName: 'Email', minWidth: 190, flex: 1 },
    ],
    []
  );

  const columnsAfter: GridColDef[] = useMemo(
    () => [
      {
        field: 'roles',
        headerName: 'Roles',
        minWidth: 170,
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const held = (params.row as MemberRow).roles;
          if (!held.length) {
            return (
              <Typography variant="body2" color="text.secondary">
                None
              </Typography>
            );
          }
          return (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap', overflow: 'hidden' }}>
              {held.map((role) => (
                <Tooltip key={role.role_id} title={role.description || ''}>
                  <Chip size="small" label={role.name} data-testid="user-role-chip" />
                </Tooltip>
              ))}
            </Box>
          );
        },
      },
      {
        field: 'security_attributes',
        headerName: 'Security Attributes',
        minWidth: 220,
        sortable: false,
        renderCell: (params) => {
          const names = attributeValues((params.row as MemberRow).metadata).map((v) =>
            valueName(v.fielddefId, v.valueId)
          );
          if (!names.length) {
            return (
              <Typography variant="body2" color="text.secondary">
                None
              </Typography>
            );
          }
          return (
            <Tooltip title={names.join(', ')}>
              <Box sx={{ display: 'flex', gap: 0.5 }} data-testid="user-attributes">
                {names.slice(0, MAX_ATTRIBUTE_CHIPS).map((name, index) => (
                  <Chip key={`${name}-${index}`} size="small" variant="outlined" label={name} />
                ))}
                {names.length > MAX_ATTRIBUTE_CHIPS && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`+${names.length - MAX_ATTRIBUTE_CHIPS}`}
                  />
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'last_login_at',
        headerName: 'Last Sign-In',
        width: 140,
        valueGetter: (_value, row) =>
          row.last_login_at ? formatAuditTimestamp(row.last_login_at) : '—',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 210,
        renderCell: (params) => {
          const row = params.row as MemberRow;
          const plain = (
            <StatusChip status={row.status} label={membershipStatusLabel(row.status)} />
          );
          if (!row.invite) return plain;
          // An invitation says when it was sent and when it expires (ORCH-420 §2.1)
          const chip = (
            <Tooltip title={inviteSummary(row.invite, formatAuditTimestamp)}>
              <span data-testid="invite-status">{plain}</span>
            </Tooltip>
          );
          const emailNotSent = !!row.email_status && row.email_status !== 'sent';
          if (!emailNotSent && !row.invite.expired) return chip;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {chip}
              <Tooltip
                title={
                  row.invite.expired
                    ? 'The link in the email no longer works. Resend the invitation.'
                    : inviteEmailNote(row)
                }
              >
                <Box
                  data-testid={row.invite.expired ? 'invite-expired' : 'invite-email-not-sent'}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: 'warning.main' }}
                >
                  <WarningAmberIcon fontSize="small" />
                  <Typography variant="caption" noWrap>
                    {row.invite.expired ? 'Invite expired' : 'Email not sent'}
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: 'updated_at',
        headerName: 'Modified',
        width: 140,
        valueGetter: (_value, row) => (row.updated_at ? formatAuditTimestamp(row.updated_at) : '—'),
      },
      {
        field: 'updated_by',
        headerName: 'Modified By',
        width: 120,
        valueGetter: (_value, row) =>
          formatAuditUser(row.updated_by, auditPerson(row.updated_by_user)),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const member = params.row as MemberRow;
          // One matrix decides what this row's status allows (ORCH-420 §3.2)
          const allowed = memberActions(member, can);
          const toggle = allowed.has('deactivate') || allowed.has('reactivate');
          return (
            <RowActions
              entityLabel="User"
              rowLabel={member.email}
              testIdPrefix="user-action"
              rowTestId={`user-actions-${member.email}`}
              onEdit={() => openMember(member)}
              secondaryActions={
                <>
                  {allowed.has('effectiveAccess') && (
                    <RowActionButton
                      label="Effective Access"
                      testId="user-action-effective-access"
                      onClick={() => openEffectiveAccess(member)}
                      icon={<VisibilityIcon fontSize="small" />}
                    />
                  )}
                  {allowed.has('resendInvite') && (
                    <RowActionButton
                      label="Resend Invite"
                      ariaLabel={`Resend Invite ${member.email}`}
                      testId="user-action-resend-invite"
                      onClick={() => resendInvite(member)}
                      icon={<MailOutlineIcon fontSize="small" />}
                    />
                  )}
                  {allowed.has('cancelInvite') && (
                    <RowActionButton
                      label="Cancel Invite"
                      ariaLabel={`Cancel Invite ${member.email}`}
                      testId="user-action-cancel-invite"
                      onClick={() => setConfirm({ kind: 'cancelOne', member })}
                      icon={<UnsubscribeIcon fontSize="small" />}
                    />
                  )}
                </>
              }
              deactivation={
                toggle
                  ? {
                      variant: 'deactivate',
                      isActive: allowed.has('deactivate'),
                      onToggle: () =>
                        allowed.has('reactivate')
                          ? reactivate(member)
                          : setConfirm({
                              kind: 'deactivate',
                              members: [member],
                              allMatching: false,
                            }),
                    }
                  : undefined
              }
              onDelete={
                allowed.has('remove') ? () => setConfirm({ kind: 'remove', member }) : undefined
              }
              deleteVerb="Remove"
            />
          );
        },
      },
    ],
    [can, openMember, openEffectiveAccess, reactivate, resendInvite, valueName]
  );

  // Memoized: a fresh object every render re-runs MDTable's effects
  const mdTableMetadata = useMemo(
    () => ({
      modelId: MEMBERSHIP_MODEL_ID,
      viewType: 'table' as const,
      tenantId,
      title: `Users — ${total} in ${tenantName}`,
      columnsBeforeRelatedFields: columnsBefore,
      columnsAfterRelatedFields: columnsAfter,
    }),
    [tenantId, tenantName, total, columnsBefore, columnsAfter]
  );

  const customToolbarFilters = (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
      {isSystemAdmin && (
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="user-tenant-label">Tenant</InputLabel>
          <Select
            labelId="user-tenant-label"
            label="Tenant"
            value={tenantOptions.some((t) => t.tenant_id === tenantId) ? tenantId : ''}
            onChange={(event) => {
              setManagedTenantId(event.target.value);
              setRoleId(null);
              setPage(1);
            }}
            data-testid="user-tenant-picker"
          >
            {tenantOptions.map((tenant) => (
              <MenuItem key={tenant.tenant_id} value={tenant.tenant_id}>
                {tenant.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <TextField
        size="small"
        label="Search"
        placeholder="Name or email"
        value={searchInput}
        onChange={(event) => {
          setSearchInput(event.target.value);
          applySearch(event.target.value);
        }}
        sx={{ minWidth: 240 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
          endAdornment: searchInput ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Clear search"
                onClick={() => {
                  setSearchInput('');
                  setSearch('');
                  setPage(1);
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        inputProps={{ 'data-testid': 'user-search' }}
      />
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel id="user-status-label" shrink>
          Status
        </InputLabel>
        <Select
          labelId="user-status-label"
          label="Status"
          notched
          displayEmpty
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
          data-testid="user-status-filter"
        >
          {STATUS_FILTERS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <RoleSelect
        multiple={false}
        offerAll
        roles={roles}
        value={roleId}
        onChange={(next) => {
          setRoleId(next);
          setPage(1);
        }}
        label="Role"
        placeholder="All Roles"
        size="small"
        dataTestId="user-role-filter"
        sx={{ minWidth: 220 }}
      />
    </Box>
  );

  // A row is selectable when some bulk action could act on it (ORCH-388 §3.3, ORCH-420)
  const isSelectable = (m: Member) => isBulkSelectable(m, can) || mayAct('passwordReset', m, can);
  const selectedMembers = members.filter((m) => selectedIds.includes(m.user_id));
  const selectedCount = allMatching ? total : selectedIds.length;
  const pageFullySelected =
    !allMatching &&
    total > members.length &&
    selectedIds.length > 0 &&
    members.filter(isSelectable).every((m) => selectedIds.includes(m.user_id));

  // An action is offered only when it applies to someone selected. With every match selected
  // the unloaded rows are unknown, so only the Status filter can rule an action out.
  const bulkApplies = (kind: BulkKind): boolean =>
    allMatching
      ? !status || actionAppliesTo(memberActionOf(kind), status)
      : selectedMembers.some((m) => mayAct(memberActionOf(kind), m, can));
  const selfId = state.user?.userId;
  const assignApplies =
    allMatching ||
    selectedMembers.some((m) => mayAct('assignRoles', m, can) && m.user_id !== selfId);
  const assignableRoles = roles.filter((r) => r.assignable === true);

  const deactivateActions = [
    {
      label: 'Deactivate',
      disabled: !bulkApplies('deactivate'),
      disabledReason: 'No active users selected',
      onClick: () => setConfirm({ kind: 'deactivate', members: selectedMembers, allMatching }),
      testId: 'user-bulk-deactivate',
      destructive: true,
    },
    {
      label: 'Reactivate',
      disabled: !bulkApplies('reactivate'),
      disabledReason: 'No deactivated users selected',
      onClick: () => setConfirm({ kind: 'reactivate', members: selectedMembers, allMatching }),
      testId: 'user-bulk-reactivate',
    },
  ];
  const inviteActions = [
    {
      label: 'Resend Invite',
      disabled: !bulkApplies('resendInvite'),
      disabledReason: 'No invitations selected',
      onClick: () => setConfirm({ kind: 'resendInvite', members: selectedMembers, allMatching }),
      testId: 'user-bulk-resend-invite',
    },
    {
      label: 'Cancel Invite',
      disabled: !bulkApplies('cancelInvite'),
      disabledReason: 'No invitations selected',
      onClick: () => setConfirm({ kind: 'cancelInvite', members: selectedMembers, allMatching }),
      testId: 'user-bulk-cancel-invite',
      destructive: true,
    },
  ];
  const assignAction = {
    label: 'Assign Role(s)',
    disabled: !assignApplies || !assignableRoles.length,
    disabledReason: !assignApplies
      ? "You can't assign roles to anyone selected"
      : 'No roles you can assign',
    onClick: () => setConfirm({ kind: 'assign', members: selectedMembers, allMatching }),
    testId: 'user-bulk-assign-roles',
  };

  const resetAction: BulkAction = {
    label: 'Reset Password',
    disabled: !bulkApplies('reset') || selectedCount > PASSWORD_RESET_BULK_MAX,
    disabledReason: bulkApplies('reset')
      ? `Select ${PASSWORD_RESET_BULK_MAX} or fewer people`
      : 'No active users selected',
    onClick: () => setConfirm({ kind: 'reset', members: selectedMembers, allMatching }),
    testId: 'user-bulk-reset-password',
  };

  const selectionBar = () => (
    <BulkActionBar
      selectedCount={selectedCount}
      working={working}
      testIdPrefix="user-bulk"
      onClear={clearSelection}
      actions={[
        ...(canAssignRoles ? [assignAction] : []),
        ...(canDeactivate ? deactivateActions : []),
        ...(canModifyInvites ? inviteActions : []),
        ...(canEdit ? [resetAction] : []),
      ]}
    >
      {pageFullySelected && (
        <Link
          component="button"
          variant="body2"
          onClick={() => setAllMatching(true)}
          data-testid="user-select-all-matching"
        >
          Select all {total} matching your filters
        </Link>
      )}
    </BulkActionBar>
  );

  // The bulk dialog names who is affected and who is skipped: "Deactivate 2 users? 1 already
  // deactivated will be skipped." A row's own Deactivate names the person.
  const bulk =
    confirm && confirm.kind in BULK_VERBS
      ? (confirm as Extract<Confirm, { kind: BulkKind }>)
      : null;
  const single = bulk && !bulk.allMatching && bulk.members.length === 1 ? bulk.members[0] : null;
  // Nobody is sent their own reset, so the caller isn't counted (ORCH-387)
  const affected = bulk
    ? bulk.members.filter(
        (m) =>
          mayAct(memberActionOf(bulk.kind), m, can) &&
          !(bulk.kind === 'reset' && m.user_id === selfId)
      ).length
    : 0;
  const bulkSkipped = bulk && !bulk.allMatching ? bulk.members.length - affected : 0;
  const bulkNoun = bulk ? BULK_VERBS[bulk.kind].noun : users;
  const confirmWho =
    confirm?.kind === 'remove' || confirm?.kind === 'cancelOne'
      ? firstLast(confirm.member)
      : single
        ? firstLast(single)
        : bulkNoun(bulk?.allMatching ? total : affected);
  const bulkSkipNote =
    bulk && bulkSkipped ? ` ${BULK_VERBS[bulk.kind].skipped(bulkSkipped)} will be skipped.` : '';
  const notActive =
    bulk?.kind === 'reset' && !bulk.allMatching
      ? bulk.members.filter((m) => !actionAppliesTo('passwordReset', m.status)).length
      : 0;
  const resetSkipNote = notActive ? ` ${notActive} not active will be skipped.` : '';
  // Wording from QA (ORCH-382, 2026-10-21): the full name as in the title, what they lose, what is
  // kept. The row's trash icon reads "Remove User" and is offered with the same right as Deactivate,
  // so a row's dialog can always name it; the bulk dialog has no remove and says who is skipped.
  const deactivateText = single
    ? `${firstLast(single)} will no longer be able to access ${tenantName}. Their profile, roles, ` +
      `and security attributes will be kept, and you can reactivate them at any time. ` +
      `To remove them from ${tenantName} instead, use Remove User.`
    : `They will no longer be able to access ${tenantName}. Their profiles, roles, and security ` +
      `attributes will be kept, and you can reactivate them at any time.${bulkSkipNote}`;

  return (
    <SkeletonPage
      pageTitle="User Manager"
      breadcrumbs={[
        { label: 'Home', href: '/home', icon: <HomeIcon fontSize="inherit" /> },
        { label: 'Admin', href: '/admin', icon: <SettingsIcon fontSize="inherit" /> },
        { label: 'User Manager', icon: <UsersIcon fontSize="inherit" /> },
      ]}
    >
      <Box>
        <MDTable
          mode="metadata"
          metadata={mdTableMetadata}
          data={rows}
          loading={loading}
          error={error}
          onRowClick={openMember}
          defaultColumnVisibility={HIDDEN_BY_DEFAULT}
          selectable={canDeactivate || canAssignRoles || canModifyInvites}
          isRowSelectable={isSelectable}
          selectedIds={selectedIds}
          onSelectionChange={(ids) => {
            setSelectedIds(ids);
            setAllMatching(false);
          }}
          selectionBar={selectionBar}
          sortable
          filterable
          showRefreshButton
          showAddButton={false}
          onRefresh={fetchMembers}
          customToolbarFilters={customToolbarFilters}
          customHeaderActions={
            canInvite ? (
              <Button
                variant="contained"
                size="small"
                onClick={() => setInviteOpen(true)}
                data-testid="user-invite-button"
              >
                + Invite
              </Button>
            ) : undefined
          }
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
            gap: 2,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="user-page-size-label">Rows Per Page</InputLabel>
            <Select
              labelId="user-page-size-label"
              value={String(perPage)}
              label="Rows Per Page"
              onChange={(event) => {
                setPerPage(Number(event.target.value));
                setPage(1);
              }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <MenuItem key={size} value={String(size)}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Pagination
            page={page}
            count={Math.max(1, Math.ceil(total / perPage))}
            onChange={(_event, value) => setPage(value)}
            color="primary"
          />
        </Box>
      </Box>

      <InviteDialog
        open={inviteOpen}
        tenantId={tenantId}
        targetTenantId={otherTenant}
        roles={roles}
        attributeFieldIds={attributeFieldIds}
        canAssignRoles={canAssignRoles}
        onClose={() => setInviteOpen(false)}
        onInvited={(summary) => {
          setInviteOpen(false);
          setNotice(summary);
          fetchMembers();
        }}
      />

      <BulkAssignRolesDialog
        open={confirm?.kind === 'assign'}
        roles={assignableRoles}
        count={confirm?.kind === 'assign' && !confirm.allMatching ? confirm.members.length : total}
        includesSelf={
          confirm?.kind === 'assign' && confirm.members.some((m) => m.user_id === selfId)
        }
        working={working}
        onAssign={assignRoles}
        onClose={() => setConfirm(null)}
      />
      <BulkRolesResultDialog
        result={rolesResult}
        roles={roles}
        onClose={() => setRolesResult(null)}
      />
      <BulkRolesResultDialog
        title={inviteResult?.title}
        result={inviteResult?.result ?? null}
        roles={roles}
        onClose={() => setInviteResult(null)}
      />

      <ConfirmDialog
        open={confirm?.kind === 'deactivate'}
        title={`Deactivate ${confirmWho}?`}
        confirmLabel="Deactivate"
        loading={working}
        onConfirm={handleConfirm}
        onClose={() => setConfirm(null)}
        dataTestId="deactivate-dialog"
      >
        <Typography variant="body2">{deactivateText}</Typography>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm?.kind === 'reactivate'}
        title={`Reactivate ${confirmWho}?`}
        confirmLabel="Reactivate"
        loading={working}
        onConfirm={handleConfirm}
        onClose={() => setConfirm(null)}
        dataTestId="reactivate-dialog"
      >
        <Typography variant="body2">
          They can sign in to {tenantName} again with the roles and security attributes they had.
          {bulkSkipNote}
        </Typography>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm?.kind === 'resendInvite'}
        title={`Resend ${confirmWho}?`}
        confirmLabel="Resend"
        loading={working}
        onConfirm={handleConfirm}
        onClose={() => setConfirm(null)}
        dataTestId="resend-invite-dialog"
      >
        <Typography variant="body2">
          Each person gets a new email with a new link, good for 7 days. Their earlier link stops
          working.{bulkSkipNote}
        </Typography>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm?.kind === 'cancelInvite' || confirm?.kind === 'cancelOne'}
        title={
          confirm?.kind === 'cancelOne'
            ? `Cancel the invitation to ${confirmWho}?`
            : `Cancel ${confirmWho}?`
        }
        confirmLabel="Cancel Invite"
        confirmColor="error"
        loading={working}
        onConfirm={handleConfirm}
        onClose={() => setConfirm(null)}
        dataTestId="cancel-invite-dialog"
      >
        <Typography variant="body2">
          {confirm?.kind === 'cancelOne'
            ? 'The link in their email will stop working. You can invite them again later.'
            : `The links in their emails will stop working. You can invite them again later.${bulkSkipNote}`}
        </Typography>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm?.kind === 'reset'}
        title={`Send password reset emails to ${confirmWho}?`}
        confirmLabel="Send Emails"
        confirmColor="primary"
        loading={working}
        onConfirm={handleConfirm}
        onClose={() => setConfirm(null)}
        dataTestId="reset-password-dialog"
      >
        <Typography variant="body2">
          Each person gets a link to choose a new password. It works once and expires in 24 hours.
          Their current password keeps working until they use it.{resetSkipNote}
        </Typography>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm?.kind === 'remove'}
        title={`Remove ${confirmWho}?`}
        confirmLabel="Remove"
        confirmColor="error"
        loading={working}
        onConfirm={handleConfirm}
        onClose={() => setConfirm(null)}
        dataTestId="remove-dialog"
      >
        <Typography variant="body2">
          {confirm?.kind === 'remove' ? givenName(confirm.member) : 'This person'} loses all access
          to {tenantName}. Their history here is kept. To pause access instead, use Deactivate.
        </Typography>
      </ConfirmDialog>

      <NotificationSnackbar
        open={!!error && !loading}
        onClose={() => setError(null)}
        message={error || ''}
        severity="error"
        autoHideDuration={6000}
      />
      <NotificationSnackbar
        open={!!notice}
        onClose={() => setNotice(null)}
        message={notice?.message || ''}
        severity={notice?.severity || 'success'}
        autoHideDuration={5000}
      />
    </SkeletonPage>
  );
};

export default UserManagementPage;
